import React, { useEffect, useRef, useState } from 'react';
import config from '../../config';
import Typography from '@material-ui/core/Typography';
import { CircularProgress, Container, TextField } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import Alert from '@material-ui/lab/Alert';
import Box from '@material-ui/core/Box';
import ConnectionProvider from '../../graphql/ConnectionProvider';
import InstallationApp from './InstallationApp';

const useStyles = makeStyles(theme => ({
  container: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    minHeight: '100%',
  },
  inputWrapper: {
    display: 'flex',
    justifyContent: 'center',
    margin: theme.spacing(0, -0.5),
    // 8 boxes must fit a phone screen: share the width evenly and cap the size,
    // instead of the default TextField width the 4-digit Homepage PIN uses.
    '& > *': {
      flex: '1 1 0',
      minWidth: 0,
      maxWidth: theme.spacing(6),
      margin: theme.spacing(0, 0.5),
    },
    '& input': {
      padding: theme.spacing(1.5, 0),
      textAlign: 'center',
      fontSize: '1.25rem',
    },
  },
}));

const PinInput = ({ value, onChange, onComplete, disabled }) => {
  const classes = useStyles();
  const refs = [useRef(), useRef(), useRef(), useRef(), useRef(), useRef(), useRef(), useRef()];
  const current = value.length < refs.length ? refs[value.length].current : undefined;

  const handleFocus = i => () => {
    if (value.length > i) {
      onChange(value.substring(0, i));
    } else if (value.length < i) {
      refs[value.length].current.focus();
    }
  };

  const handleChange = e => {
    const nextValue = e.target.value.toUpperCase().replace(/[^A-Z0-9]+/g, '');
    onChange(value + nextValue.slice(0, 8 - value.length));
  };

  const handleKeydown = i => e => {
    const key = e.keyCode || e.charCode;
    if (key === 8 || key === 46) {
      refs[i].current.focus();
      if (value.length > 0) {
        onChange(value.slice(0, i));
      }
    }
  };

  useEffect(() => {
    if (current && !disabled) {
      current.focus();
    }
    if (value.length === 8) {
      onComplete(value);
    }
  }, [current, disabled, value, onComplete]);

  return (
    <div className={classes.inputWrapper}>
      {refs.map((ref, i) => (
        <TextField
          variant="outlined"
          key={i}
          inputRef={ref}
          inputProps={{ autoFocus: i === 0, onKeyDown: handleKeydown(i) }}
          onFocus={handleFocus(i)}
          value={value[i] || ''}
          onChange={handleChange}
          disabled={disabled}
        />
      ))}
    </div>
  );
};

const storageKey = organizationId => `installation-code-${organizationId}`;

const InstallationPage = ({ match }) => {
  const classes = useStyles();
  const organizationId = parseInt(match.params.organizationId, 10);
  const [token, setToken] = useState(() => window.localStorage.getItem(storageKey(organizationId)));
  const [value, setValue] = useState('');
  const [error, setError] = useState();
  const [busy, setBusy] = useState(false);
  const [revoked, setRevoked] = useState(false);
  const [wrongHospital, setWrongHospital] = useState(false);

  const lock = () => {
    window.localStorage.removeItem(storageKey(organizationId));
    setToken(null);
    setValue('');
  };

  const logIn = async code => {
    setBusy(true);
    try {
      const result = await fetch(`${config.apiURL}/identity/session?pin=${encodeURIComponent(code)}`);
      if (result.ok) {
        window.localStorage.setItem(storageKey(organizationId), code);
        setToken(code);
        setRevoked(false);
        setWrongHospital(false);
        setError(undefined);
      } else {
        setError('Code is not valid. Ask a Super Admin for an active installation code.');
        setRevoked(false);
      }
    } catch (e) {
      setError('Unexpected error, please try again later.');
    } finally {
      setValue('');
      setBusy(false);
    }
  };

  if (token && isFinite(organizationId)) {
    return (
      <ConnectionProvider
        accessToken={token}
        organizationId={organizationId}
        onUnauthorized={() => {
          setRevoked(true);
          lock();
        }}
      >
        <InstallationApp
          organizationId={organizationId}
          onIdle={lock}
          onAccessError={() => {
            setWrongHospital(true);
            lock();
          }}
        />
      </ConnectionProvider>
    );
  }

  return (
    <Container maxWidth="sm" className={classes.container}>
      <Typography variant="h1" align="center" gutterBottom>
        Installation access
      </Typography>
      <Typography align="center" color="textSecondary" gutterBottom>
        Enter the code issued for this hospital.
      </Typography>
      <Box my={2}>
        <PinInput value={value} onChange={setValue} onComplete={logIn} disabled={busy} />
      </Box>
      <Box my={1} textAlign="center">
        {busy && <CircularProgress />}
        {!busy && error && <Alert severity="error">{error}</Alert>}
        {!busy && revoked && (
          <Alert severity="warning">This code is no longer active. Ask a Super Admin to issue a new one.</Alert>
        )}
        {!busy && !error && wrongHospital && (
          <Alert severity="error">
            This code was issued for a different hospital. Check the link you were given or ask a Super Admin for the
            right code.
          </Alert>
        )}
      </Box>
    </Container>
  );
};

export default InstallationPage;
